/**
 * <VaultProgressPanel /> — Phase 4a improvement A.
 *
 * Compact progress panel shown at the top of the Continuity System vault
 * landing for gateway users. Renders the four Premium-qualifying goals as a
 * check-off list with a progress bar that fills 0/4 → 4/4. Reads the same
 * live source of truth as the header 0/4 pill and the Upgrade modal.
 */
import React from 'react';
import { Check, Circle } from 'lucide-react';
import { useQualifyingCriteria } from '../../hooks/useQualifyingCriteria';

const GOALS = [
  { key: 'continuity_created', label: 'Continuity System activated' },
  { key: 'asset_value_met', label: 'Assets ≥ $500,000 recorded' },
  { key: 'contacts_and_docs_met', label: 'Trusted contacts + documents in place' },
  { key: 'completeness_met', label: 'Portfolio 75% complete' },
];

export function VaultProgressPanel() {
  const { data, loading } = useQualifyingCriteria(true);
  if (loading || !data) return null;
  const c = data.criteria || {};
  const met = GOALS.reduce((n, g) => n + (c[g.key] ? 1 : 0), 0);
  const total = GOALS.length;
  const pct = Math.round((met / total) * 100);
  const complete = met === total;

  return (
    <div
      className="rounded-xl border border-emerald-100 bg-emerald-50/40 p-4 sm:p-5 mb-4"
      data-testid="vault-progress-panel"
    >
      <div className="flex items-center justify-between mb-2 flex-wrap gap-1">
        <h3 className="text-sm font-semibold text-slate-900">
          {complete ? 'You qualify for Portfolio Clarity' : 'Your progress toward Portfolio Clarity'}
        </h3>
        <span className="text-xs font-mono font-semibold text-emerald-800" data-testid="vault-progress-count">
          {met}/{total} · {pct}%
        </span>
      </div>
      <div className="h-2 w-full rounded-full bg-white border border-emerald-100 overflow-hidden mb-3">
        <div
          className="h-full bg-emerald-600 transition-all duration-500"
          style={{ width: `${pct}%` }}
          data-testid="vault-progress-bar"
        />
      </div>
      <ul className="space-y-1.5">
        {GOALS.map(({ key, label }) => {
          const done = !!c[key];
          const Icon = done ? Check : Circle;
          return (
            <li key={key} className="flex items-center gap-2 text-xs">
              <Icon className={`w-3.5 h-3.5 shrink-0 ${done ? 'text-emerald-700' : 'text-slate-400'}`} strokeWidth={done ? 3 : 2} />
              <span className={done ? 'text-slate-900 font-medium' : 'text-slate-600'}>{label}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export default VaultProgressPanel;
